import qz from 'qz-tray'
import config from '../../config.json'
import PreferenceService from '../../DTO/PreferencesDTO'
import { PRINT_PREF_KEY1, PrintSettingGroup, findPrintFormat, mediaPreferenceKey, parsePrintDpi, printerPreferenceKey, qualityPreferenceKey } from '../DTO/PrintFormats'

let securityConfigured = false

function asMessage(error: unknown): string {
  if (error instanceof Error) { return error.message }
  if (typeof error === 'string') { return error }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }
  return ''
}

export function qzErrorMessage(error: unknown): string {
  const raw = asMessage(error)
  if (/websocket|connection refused|ECONNREFUSED|8181|Unable to establish/i.test(raw)) {
    return 'QZ Tray ist nicht erreichbar. Bitte QZ Tray starten.'
  }
  return raw || 'Drucken mit QZ Tray ist fehlgeschlagen.'
}

async function configureSecurity() {
  if (securityConfigured) { return }
  await PreferenceService.init()
  const cert = PreferenceService.getVal(PRINT_PREF_KEY1, 'qzCertificate').trim()
  if (!cert) {
    securityConfigured = true
    return
  }
  qz.security.setCertificatePromise((resolve: (value: string) => void, reject: (reason?: unknown) => void) => {
    fetch(`${config.apiPrefix}qz/certificate`)
      .then((response) => {
        if (!response.ok) {
          reject(response.statusText)
          return
        }
        return response.text()
      })
      .then((text) => {
        if (text !== undefined) { resolve(text) }
      })
      .catch(reject)
  })
  qz.security.setSignatureAlgorithm('SHA512')
  qz.security.setSignaturePromise((toSign: string) => {
    return (resolve: (value: string) => void, reject: (reason?: unknown) => void) => {
      fetch(`${config.apiPrefix}qz/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request: toSign }),
      })
        .then((response) => {
          if (!response.ok) {
            reject(response.statusText)
            return
          }
          return response.text()
        })
        .then((text) => {
          if (text !== undefined) { resolve(text) }
        })
        .catch(reject)
    }
  })
  securityConfigured = true
}

export type QzPrintJob = {
  url: string,
  formatValue: string,
  formatTitle: string,
  widthMm: number,
  heightMm: number,
  orientation: 'portrait' | 'landscape',
  sides: 'simplex' | 'duplex',
  color: 'color' | 'gray',
  quality: string,
}

export function qzJobFromPrintGroup<T>(group: PrintSettingGroup<T>, url: string): QzPrintJob {
  return {
    url,
    formatValue: group.formatValue,
    formatTitle: group.formatTitle,
    widthMm: group.widthMm,
    heightMm: group.heightMm,
    orientation: group.orientation,
    sides: group.sides,
    color: group.color,
    quality: group.quality,
  }
}

export async function printPdfWithQz(job: QzPrintJob) {
  await configureSecurity()
  const printer = PreferenceService.getVal(PRINT_PREF_KEY1, printerPreferenceKey(job.formatValue)).trim()
  if (!printer) {
    throw new Error(`Kein Drucker für ${job.formatTitle} hinterlegt.`)
  }
  const media = PreferenceService.getVal(PRINT_PREF_KEY1, mediaPreferenceKey(job.formatValue)).trim()
    || findPrintFormat(job.formatValue)?.defaultMedia
    || ''
  const dpi = parsePrintDpi(job.quality) ?? parsePrintDpi(PreferenceService.getVal(PRINT_PREF_KEY1, qualityPreferenceKey(job.formatValue)))
  if (!qz.websocket.isActive()) {
    await qz.websocket.connect()
  }
  // Feste Medianamen (A6, A4, …) statt Millimeter: Custom-mm liefert am HP-Treiber
  // Papierflächen mit negativem Ursprung und skaliert faktisch auf A4.
  const printConfig = qz.configs.create(printer, {
    copies: 1,
    units: 'mm',
    orientation: job.orientation,
    scaleContent: true,
    margins: 0,
    colorType: job.color === 'gray' ? 'grayscale' : 'color',
    duplex: job.sides === 'duplex',
    rasterize: !!dpi,
    ...(dpi ? { density: dpi / 25.4 } : {}),
    size: media ? media : { width: job.widthMm, height: job.heightMm },
  })
  await qz.print(printConfig, [{
    type: 'pixel',
    format: 'pdf',
    flavor: 'file',
    data: job.url,
  }])
}
